import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Archive, ArchiveRestore, Loader2, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../../../lib/supabase';
import { errorMessage } from '../../../../lib/errors';

/**
 * Archive a customer, or put them back.
 *
 * Archiving is the answer to "this person has died / is barred / sold their car
 * and moved away" — the three reasons a workshop needs a customer off the list.
 * It is NOT a delete, and this screen says so in as many words, because the two
 * get confused and only one of them is reversible:
 *
 *   * Their invoices, ledger entries and service history are untouched. A sole
 *     trader has to keep those six years for HMRC, and service_history's
 *     customer_id is ON DELETE SET NULL — an actual delete would strip the work
 *     history off records the money is still attached to.
 *   * They stop appearing in search, the customer list and the follow-up lanes,
 *     and — the part that matters most — automated MOT reminder emails stop.
 *     That gating lives in the database, not here; see
 *     20260904120000_archive_customers.sql.
 *
 * Erasing someone's personal data under UK GDPR Art. 17 is a different, final
 * operation and is deliberately not this button.
 */
interface ArchiveCustomerModalProps {
    onClose: () => void;
    customer: { id: string; full_name: string; archived_at?: string | null };
    /** Called after the flag has actually changed, so the caller can refetch. */
    onDone: () => void;
}

/**
 * Mounted only while it is open — there is no `isOpen` prop.
 *
 * The obvious shape is `isOpen` plus an effect that clears the reason field and
 * the error each time it opens, and that is what this was. It is also a
 * setState inside an effect body: a cascading render for a fact React can
 * already get for free. Mounting on demand means the state starts empty every
 * time because it is genuinely new state.
 */
export default function ArchiveCustomerModal({ onClose, customer, onDone }: ArchiveCustomerModalProps) {
    const archived = Boolean(customer.archived_at);
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = async () => {
        setSaving(true);
        setError(null);
        try {
            const { error: rpcError } = await supabase.rpc('fn_set_customer_archived', {
                p_customer_id: customer.id,
                p_archived: !archived,
                p_reason: archived ? null : (reason.trim() || null),
            });
            if (rpcError) throw rpcError;

            toast.success(archived ? 'Customer restored.' : 'Customer archived.');
            onDone();
            onClose();
        } catch (err) {
            setError(errorMessage(err) || 'Could not update this customer.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-scrim/80 backdrop-blur-sm animate-in fade-in">
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="w-full max-w-md max-h-[92vh] flex flex-col bg-panel border border-line rounded-t-xl sm:rounded-xl shadow-2xl overflow-hidden"
            >
                <div className="p-6 pb-4 relative shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="absolute top-3 right-3 min-h-[44px] min-w-[44px] flex items-center justify-center text-fg-4 hover:text-fg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                    <h2 className="text-xl font-bold text-fg pr-12 flex items-center gap-2">
                        {archived ? <ArchiveRestore className="w-5 h-5 shrink-0" /> : <Archive className="w-5 h-5 shrink-0" />}
                        {archived ? 'Restore customer' : 'Archive customer'}
                    </h2>
                    <p className="mt-1 text-sm text-fg-3 break-words">{customer.full_name}</p>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto px-6 space-y-4">
                    {archived ? (
                        <p className="text-sm text-fg-2">
                            They will show up in search and your customer list again, and their
                            MOT reminders and follow-ups resume.
                        </p>
                    ) : (
                        <>
                            <div className="rounded-lg border border-line bg-tint/[0.02] p-4 space-y-2 text-sm">
                                <p className="font-bold text-fg">What archiving does</p>
                                <ul className="space-y-1.5 text-fg-3">
                                    <li>• Hides them from search, your customer list and follow-ups.</li>
                                    <li>• Stops automatic MOT reminder emails to them.</li>
                                    <li>• Keeps every invoice, payment and service record exactly as it is.</li>
                                    <li>• Leaves their vehicles on file — a car can change hands.</li>
                                </ul>
                            </div>

                            {/* Said plainly, because "archive" is the button people
                                press when they mean "delete", and the difference
                                only becomes obvious when it is too late. */}
                            <p className="text-xs text-fg-4">
                                You can undo this at any time. If you have been asked to erase
                                someone's personal details rather than hide them, that is a
                                different job — archiving keeps their data.
                            </p>

                            <div>
                                <label htmlFor="archive-reason" className="block text-xs font-bold text-fg-4 uppercase mb-1">
                                    Reason <span className="normal-case font-normal text-fg-4">(optional)</span>
                                </label>
                                <input
                                    id="archive-reason"
                                    type="text"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-3 text-fg text-sm focus:ring-2 focus:ring-accent outline-none placeholder-fg-4"
                                    placeholder="Moved away, no longer a customer…"
                                    maxLength={200}
                                />
                                <p className="mt-1.5 text-xs text-fg-4">
                                    Shown on their profile so you are not left guessing later.
                                </p>
                            </div>
                        </>
                    )}

                    {error && (
                        <div className="p-3 bg-danger/10 border border-danger/20 rounded-lg flex items-start gap-2 text-danger text-sm">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span className="min-w-0">{error}</span>
                        </div>
                    )}
                </div>

                <div className="shrink-0 p-6 pt-4 space-y-2 border-t border-tint/5 bg-panel">
                    <button
                        type="button"
                        onClick={() => void submit()}
                        disabled={saving}
                        className={`w-full font-bold py-3.5 min-h-[44px] rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 ${
                            archived
                                ? 'fd-btn-primary'
                                : 'bg-gold-fill/15 text-gold border border-gold/30 hover:bg-gold-fill/25'
                        }`}
                    >
                        {saving
                            ? <><Loader2 className="w-5 h-5 animate-spin" />Saving…</>
                            : archived
                                ? <><ArchiveRestore className="w-5 h-5" />Restore this customer</>
                                : <><Archive className="w-5 h-5" />Archive this customer</>}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="w-full min-h-[44px] text-sm font-bold text-fg-3 hover:text-fg transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                </div>
            </motion.div>
        </div>
    );
}
